import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getInitials, getName } from '../../utils/userUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Settings, LogOut, ChevronDown, LayoutDashboard } from 'lucide-react';
import AppContext from '../../contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from '../../utils/permissionUtils';

const Header = ({ username, logo, serviceDeskName, onLogout }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useContext(AppContext);
  const hasDashboardAccess = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_ACCESS);

  const handleAdminClick = () => {
    setDropdownOpen(false);
    navigate('/admin');
  };

  const handleDashboardClick = () => {
    setDropdownOpen(false);
    navigate('/admin');
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    onLogout();
  };

  return (
    <nav className="bg-black h-[80px] flex items-center">
      <div className="w-full max-w-screen-md mx-auto px-4 h-full flex items-center justify-between">
          <div className="flex items-center gap-2 rounded-full">
          {logo && (
            <Link to="/home">
              <img src={logo} alt={serviceDeskName} className="w-[120px] h-auto" />
            </Link>
          )}
          {!logo && (
            <Link to="/home">
              <img src="/logo_gps_white.png" alt={'Globe Perfect Store'} className="w-[120px] h-auto" />
            </Link>
          )}
          </div>
        <div className="flex justify-between items-center gap-4">
          {username && (
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button className="flex flex-row gap-1 items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none">
                  <span className="text-gray-400 text-base text-[14px] font-medium">
                    {getName(username)}
                  </span>
                  <div className="pb-1 rounded-full w-8 h-8">
                    <div className="w-8 h-8 rounded-full bg-[#CDC4BA] flex items-center justify-center">
                      <span className="text-white text-xs font-medium">
                        {getInitials(username)}
                      </span>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{getName(username)}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {currentUser?.email || username}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {hasDashboardAccess && (
                  <>
                    <DropdownMenuItem onClick={handleDashboardClick}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      <span>Tableau de bord Admin</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Déconnexion</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </nav>
  );
}

Header.propTypes = {
  username: PropTypes.string.isRequired,
  logo: PropTypes.string,
  serviceDeskName: PropTypes.string,
  onLogout: PropTypes.func.isRequired,
};

export { Header };